import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Layers, XCircle, Scan, ListChecks, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "./constants";

export const QuickCategoryEditor = ({ 
  open, 
  onClose, 
  categories, 
  departments, 
  onProductUpdated,
  keepLocked,
  setKeepLocked,
  lockedCategory,
  setLockedCategory,
  lockedDepartment,
  setLockedDepartment
}) => {
  const [activeTab, setActiveTab] = useState("single"); // "single" or "batch"
  const [pluInput, setPluInput] = useState("");
  const [currentProduct, setCurrentProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const pluInputRef = useRef(null);
  
  // Batch mode state
  const [batchCategory, setBatchCategory] = useState("");
  const [batchDepartment, setBatchDepartment] = useState("");
  const [batchFilteredDepartments, setBatchFilteredDepartments] = useState([]);
  const [scannedProducts, setScannedProducts] = useState([]);
  const [batchSaving, setBatchSaving] = useState(false);
  const batchInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (activeTab === "single" && pluInputRef.current) {
        setTimeout(() => pluInputRef.current?.focus(), 100);
      } else if (activeTab === "batch" && batchInputRef.current) {
        setTimeout(() => batchInputRef.current?.focus(), 100);
      }
    }
    if (open && keepLocked && lockedCategory) {
      setSelectedCategory(lockedCategory);
      setSelectedDepartment(lockedDepartment);
    }
  }, [open, activeTab, keepLocked, lockedCategory, lockedDepartment]);

  // Single mode: filter departments by category
  useEffect(() => {
    if (selectedCategory) {
      const cat = categories.find(c => c.name === selectedCategory);
      if (cat) {
        const depts = departments.filter(d => d.category_id === cat.id);
        setFilteredDepartments(depts);
      } else {
        setFilteredDepartments([]);
      }
    } else {
      setFilteredDepartments([]);
    }
  }, [selectedCategory, categories, departments]);

  // Batch mode: filter departments by category
  useEffect(() => {
    if (batchCategory) {
      const cat = categories.find(c => c.name === batchCategory);
      if (cat) {
        const depts = departments.filter(d => d.category_id === cat.id);
        setBatchFilteredDepartments(depts);
      } else {
        setBatchFilteredDepartments([]);
      }
    } else {
      setBatchFilteredDepartments([]);
    }
  }, [batchCategory, categories, departments]);

  const handlePluSearch = async (e) => {
    if (e.key === "Enter" && pluInput.trim()) {
      const searchTerm = pluInput.trim();
      
      try {
        const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(searchTerm)}&limit=50`);
        const results = response.data;
        
        let found = results.find(
          p => p.plu_code === searchTerm || 
               p.plu_code?.toLowerCase() === searchTerm.toLowerCase()
        );
        
        if (!found) {
          found = results.find(
            p => p.barcode === searchTerm ||
                 p.barcode?.toLowerCase() === searchTerm.toLowerCase() ||
                 (p.barcodes && p.barcodes.some(b => b === searchTerm || b.toLowerCase() === searchTerm.toLowerCase()))
          );
        }
        
        if (!found && results.length > 0) {
          const startMatch = results.find(p => 
            p.plu_code?.startsWith(searchTerm) || 
            p.name?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (startMatch) {
            found = startMatch;
          }
        }
        
        if (found) {
          setCurrentProduct(found);
          if (keepLocked && lockedCategory) {
            setSelectedCategory(lockedCategory);
            setSelectedDepartment(lockedDepartment);
          } else {
            setSelectedCategory(found.category || "");
            setSelectedDepartment(found.department || "");
          }
          toast.success(`Found: ${found.name}`);
        } else {
          toast.error(`Product not found: ${searchTerm}`);
          setCurrentProduct(null);
        }
      } catch (error) {
        console.error("Error searching product:", error);
        toast.error(`Error searching for product: ${searchTerm}`);
      }
      
      setPluInput("");
    }
  };

  // Batch mode: scan product and add to list
  const handleBatchScan = async (e) => {
    if (e.key === "Enter" && pluInput.trim()) {
      const searchTerm = pluInput.trim();
      
      // Check if already in list
      if (scannedProducts.some(p => p.plu_code === searchTerm || p.barcode === searchTerm)) {
        toast.info("Product already in list");
        setPluInput("");
        return;
      }
      
      try {
        const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(searchTerm)}&limit=50`);
        const results = response.data;
        
        let found = results.find(
          p => p.plu_code === searchTerm || 
               p.plu_code?.toLowerCase() === searchTerm.toLowerCase()
        );
        
        if (!found) {
          found = results.find(
            p => p.barcode === searchTerm ||
                 p.barcode?.toLowerCase() === searchTerm.toLowerCase() ||
                 (p.barcodes && p.barcodes.some(b => b === searchTerm || b.toLowerCase() === searchTerm.toLowerCase()))
          );
        }
        
        if (!found && results.length > 0) {
          const startMatch = results.find(p => 
            p.plu_code?.startsWith(searchTerm) || 
            p.name?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (startMatch) {
            found = startMatch;
          }
        }
        
        if (found) {
          // Check if already in list by ID
          if (scannedProducts.some(p => p.id === found.id)) {
            toast.info("Product already in list");
          } else {
            setScannedProducts(prev => [...prev, found]);
            toast.success(`Added: ${found.name}`);
          }
        } else {
          toast.error(`Product not found: ${searchTerm}`);
        }
      } catch (error) {
        console.error("Error searching product:", error);
        toast.error(`Error searching for product: ${searchTerm}`);
      }
      
      setPluInput("");
    }
  };

  const handleSave = async () => {
    if (!currentProduct) return;
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    
    setSaving(true);
    try {
      const response = await saveWithRetry(() => axios.put(`${API}/products/${currentProduct.id}`, {
        category: selectedCategory,
        department: selectedDepartment || ""
      }));
      
      if (response.data) {
        setRecentUpdates(prev => [{
          name: currentProduct.name,
          plu: currentProduct.plu_code,
          category: selectedCategory || '-',
          department: selectedDepartment || '-',
          time: new Date().toLocaleTimeString()
        }, ...prev.slice(0, 9)]);
        
        toast.success(`Updated: ${currentProduct.name} → ${selectedCategory}${selectedDepartment ? ' / ' + selectedDepartment : ''}`);
        
        if (keepLocked) {
          setLockedCategory(selectedCategory);
          setLockedDepartment(selectedDepartment);
        }
        
        setCurrentProduct(null);
        
        if (!keepLocked) {
          setSelectedCategory("");
          setSelectedDepartment("");
        }
        
        if (onProductUpdated) {
          onProductUpdated();
        }
        
        setTimeout(() => pluInputRef.current?.focus(), 100);
      }
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error(errorMessage(error, "Failed to save category/department"));
    } finally {
      setSaving(false);
    }
  };

  // Batch mode: Apply category/department to all scanned products
  const handleBatchApply = async () => {
    if (scannedProducts.length === 0) {
      toast.error("No products scanned");
      return;
    }
    if (!batchCategory) {
      toast.error("Please select a category");
      return;
    }
    
    setBatchSaving(true);
    try {
      const productIds = scannedProducts.map(p => p.id);
      
      const response = await saveWithRetry(() => axios.post(`${API}/products/bulk-assign-category-selected`, {
        product_ids: productIds,
        category: batchCategory,
        department: batchDepartment || null
      }));
      
      toast.success(response.data.message || `Updated ${productIds.length} products`);
      
      // Add to recent updates
      scannedProducts.forEach(product => {
        setRecentUpdates(prev => [{
          name: product.name,
          plu: product.plu_code,
          category: batchCategory || '-',
          department: batchDepartment || '-',
          time: new Date().toLocaleTimeString()
        }, ...prev].slice(0, 20));
      });
      
      // Clear scanned list
      setScannedProducts([]);
      
      if (onProductUpdated) {
        onProductUpdated();
      }
      
      setTimeout(() => batchInputRef.current?.focus(), 100);
    } catch (error) {
      console.error("Error batch saving:", error);
      toast.error(errorMessage(error, "Failed to update products"));
    } finally {
      setBatchSaving(false);
    }
  };

  const removeFromBatch = (productId) => {
    setScannedProducts(prev => prev.filter(p => p.id !== productId));
  };

  const clearBatch = () => {
    setScannedProducts([]);
  };

  const handleKeepChange = (checked) => {
    setKeepLocked(checked);
    if (checked && selectedCategory) {
      setLockedCategory(selectedCategory);
      setLockedDepartment(selectedDepartment);
    } else if (!checked) {
      setLockedCategory("");
      setLockedDepartment("");
    }
  };

  const handleClose = () => {
    setPluInput("");
    setCurrentProduct(null);
    if (!keepLocked) {
      setSelectedCategory("");
      setSelectedDepartment("");
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" />
            Quick Category/Department Edit
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" className="flex items-center gap-2">
              <Scan className="h-4 w-4" />
              Single Scan
            </TabsTrigger>
            <TabsTrigger value="batch" className="flex items-center gap-2">
              <ListChecks className="h-4 w-4" />
              Batch Scan
            </TabsTrigger>
          </TabsList>

          {/* Single Scan Mode (Original) */}
          <TabsContent value="single" className="flex-1 overflow-y-auto space-y-4 mt-4">
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${keepLocked ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
              <input
                type="checkbox"
                id="keepCategoryDept"
                checked={keepLocked}
                onChange={(e) => handleKeepChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <Label htmlFor="keepCategoryDept" className={`text-sm font-medium cursor-pointer ${keepLocked ? 'text-green-700' : 'text-gray-600'}`}>
                Keep Category & Department locked
              </Label>
              {keepLocked && lockedCategory && (
                <span className="ml-auto text-xs text-green-700 bg-green-100 px-2 py-1 rounded font-medium">
                  {lockedCategory}{lockedDepartment ? ` → ${lockedDepartment}` : ''}
                </span>
              )}
            </div>

            <div>
              <Label>Scan or Enter PLU Code</Label>
              <Input
                ref={pluInputRef}
                value={pluInput}
                onChange={(e) => setPluInput(e.target.value)}
                onKeyDown={handlePluSearch}
                placeholder="Scan barcode or enter PLU..."
                className="mt-1 font-mono text-lg"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">Press Enter to search</p>
            </div>

            {currentProduct && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-semibold text-purple-900">{currentProduct.name}</p>
                    <p className="text-sm text-purple-700">PLU: {currentProduct.plu_code}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCurrentProduct(null);
                      if (!keepLocked) {
                        setSelectedCategory("");
                        setSelectedDepartment("");
                      }
                      pluInputRef.current?.focus();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>

                <div className="text-sm text-gray-600 mb-3 p-2 bg-white rounded">
                  <span className="font-medium">Current: </span>
                  {currentProduct.category || 'No category'} → {currentProduct.department || 'No department'}
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">New Category</Label>
                    <Select
                      value={selectedCategory}
                      onValueChange={(value) => {
                        setSelectedCategory(value);
                        setSelectedDepartment("");
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm">New Department</Label>
                    <Select
                      value={selectedDepartment}
                      onValueChange={setSelectedDepartment}
                      disabled={!selectedCategory || filteredDepartments.length === 0}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={
                          !selectedCategory 
                            ? "Select category first" 
                            : filteredDepartments.length === 0 
                              ? "No departments available" 
                              : "Select department"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {[...filteredDepartments].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((dept) => (
                          <SelectItem key={dept.id} value={dept.name}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleSave}
                    disabled={saving || !selectedCategory}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {saving ? "Saving..." : "Save & Next"}
                  </Button>
                </div>
              </div>
            )}

            {recentUpdates.length > 0 && (
              <div>
                <Label className="text-sm text-gray-600">Recent Updates ({recentUpdates.length}):</Label>
                <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg divide-y">
                  {recentUpdates.slice(0, 5).map((update, idx) => (
                    <div key={idx} className="p-2 text-sm bg-green-50">
                      <div className="flex justify-between items-center">
                        <span className="font-medium truncate flex-1">{update.plu} - {update.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{update.time}</span>
                      </div>
                      <div className="text-xs text-green-600 mt-1">
                        ✓ {update.category}{update.department !== '-' ? ` → ${update.department}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Batch Scan Mode */}
          <TabsContent value="batch" className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Category/Department Selection - Locked at top */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-purple-900">Category *</Label>
                  <Select value={batchCategory} onValueChange={(v) => { setBatchCategory(v); setBatchDepartment(""); }}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category first" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-purple-900">Department</Label>
                  <Select 
                    value={batchDepartment} 
                    onValueChange={(v) => setBatchDepartment(v === "__none__" ? "" : v)}
                    disabled={!batchCategory || batchFilteredDepartments.length === 0}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={!batchCategory ? "Select category first" : "Optional"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- No Department --</SelectItem>
                      {[...batchFilteredDepartments].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((dept) => (
                        <SelectItem key={dept.id} value={dept.name}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Scan Input */}
            <div className="mb-3">
              <Label>Scan Products (rapid fire)</Label>
              <Input
                ref={batchInputRef}
                value={pluInput}
                onChange={(e) => setPluInput(e.target.value)}
                onKeyDown={handleBatchScan}
                placeholder="Scan barcode or enter PLU - they accumulate below..."
                className="mt-1 font-mono text-lg"
                autoComplete="off"
                disabled={!batchCategory}
              />
              {!batchCategory && (
                <p className="text-xs text-orange-500 mt-1">Select a category first to start scanning</p>
              )}
            </div>

            {/* Scanned Products List */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">
                  Scanned Products 
                  <Badge variant="secondary" className="ml-2">{scannedProducts.length}</Badge>
                </Label>
                {scannedProducts.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={clearBatch} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto border rounded-lg bg-gray-50">
                {scannedProducts.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                    <Scan className="h-5 w-5 mr-2" />
                    Start scanning products...
                  </div>
                ) : (
                  <div className="divide-y">
                    {scannedProducts.map((product, idx) => (
                      <div key={product.id} className="p-2 flex items-center justify-between hover:bg-gray-100">
                        <div className="flex-1">
                          <span className="text-xs text-gray-400 mr-2">#{idx + 1}</span>
                          <span className="font-medium text-sm">{product.plu_code}</span>
                          <span className="text-gray-600 text-sm ml-2">{product.name}</span>
                          {product.category && (
                            <Badge variant="outline" className="ml-2 text-[10px]">{product.category}</Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeFromBatch(product.id)}
                          className="text-gray-400 hover:text-red-500 h-6 w-6 p-0"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Apply Button */}
            <Button 
              onClick={handleBatchApply}
              disabled={batchSaving || scannedProducts.length === 0 || !batchCategory}
              className="mt-3 w-full bg-purple-600 hover:bg-purple-700"
            >
              {batchSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <ListChecks className="h-4 w-4 mr-2" />
                  Apply to {scannedProducts.length} Products → {batchCategory || '...'}
                  {batchDepartment && ` / ${batchDepartment}`}
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
